import { useState } from "react";
import { SITE_URL } from "@/config/site";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Copy,
  Check,
  Megaphone,
  Code2,
  Scale,
  Users,
  ShoppingCart,
  GraduationCap,
  PenLine,
  BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

/**
 * Public, SEO-oriented prompt library. Curated prompts grouped by profession
 * (the market buys by métier, not by model — cf. CLAUDE.md §7 / Phase 8 kits).
 * Free organic-acquisition surface: ranks for "prompts <métier> IA" queries and
 * funnels to /register. Prompt bodies are French (primary francophone/maghrébin
 * target); the page chrome is i18n'd and the auto-i18n workflow backfills the
 * other locales from EN.
 */
const ICONS = {
  marketing: Megaphone,
  dev: Code2,
  legal: Scale,
  hr: Users,
  ecommerce: ShoppingCart,
  education: GraduationCap,
  writing: PenLine,
  data: BarChart3,
};

function PromptCard({ title, body }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — no-op, the text stays visible to select manually */
    }
  };

  return (
    <div className="glass rounded-2xl p-5 flex flex-col">
      <h3 className="font-semibold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed whitespace-pre-wrap flex-1">
        {body}
      </p>
      <button
        type="button"
        onClick={copy}
        data-testid="prompt-copy"
        aria-label={copied ? t("prompts.copied") : t("prompts.copy")}
        className="mt-4 inline-flex items-center justify-center gap-2 self-start min-h-[44px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 active:bg-white/15 px-4 text-sm text-white transition-colors"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-primary" />
        )}
        {copied ? t("prompts.copied") : t("prompts.copy")}
      </button>
    </div>
  );
}

const PromptsPage = () => {
  const { t } = useTranslation();
  // Prompt library is i18n-driven (prompts.categories[], 15 languages). Icons
  // stay in code (ICONS), keyed by category key.
  const rawCats = t("prompts.categories", { returnObjects: true });
  const categories = Array.isArray(rawCats) ? rawCats : [];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: t("prompts.title"),
    description: t("prompts.meta"),
    url: `${SITE_URL}/prompts`,
    isPartOf: {
      "@type": "WebSite",
      name: "Stronger Bot",
      url: SITE_URL,
    },
  };

  return (
    <PageLayout>
      <SEO
        title={t("prompts.title")}
        description={t("prompts.meta")}
        canonical="/prompts"
        jsonLd={jsonLd}
      />
      <section className="section-y-sm px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="animate-in-up font-heading text-fluid-h1 font-bold text-white mb-4">
              {t("prompts.title")}
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto">
              {t("prompts.subtitle")}
            </p>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto mt-4">
              {t("prompts.intro")}
            </p>
          </div>

          <div className="space-y-12">
            {categories.map(({ key, prompts }) => {
              const Icon = ICONS[key] || Megaphone;
              return (
                <div key={key} data-testid={`prompt-category-${key}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">
                      {t(`prompts.cat.${key}`)}
                    </h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {(prompts || []).map((p) => (
                      <PromptCard key={p.title} title={p.title} body={p.body} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-16 glass rounded-3xl p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">
              {t("prompts.ctaTitle")}
            </h2>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              {t("prompts.ctaDesc")}
            </p>
            <Link to="/register">
              <Button
                size="lg"
                className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8"
              >
                {t("landing.cta")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default PromptsPage;
